import React, { useCallback, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// What the doctors are treating. Compound diagnoses written as "HTN, DM + APD" are split so each
// condition counts on its own - the same behaviour the old reporting tool had.

const COLUMNS = [
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'mentions', label: 'Prescriptions', align: 'right' },
  { key: 'uniquePatients', label: 'Unique Patients', align: 'right' },
  { key: 'share', label: 'Share', align: 'right', render: (r) => `${r.share}%`, csv: (r) => r.share },
];

const TopDiagnosesReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getTopDiagnoses({ from: range.from, to: range.to, limit: 30 }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  const top15 = (data?.rows || []).slice(0, 15).map((r) => ({ ...r, mentions: Number(r.mentions) }));

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <ChartPanel title="Top 15 diagnoses">
        {loading || top15.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No diagnoses recorded in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, top15.length * 30)}>
            <BarChart data={top15} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} allowDecimals={false} />
              <YAxis type="category" dataKey="diagnosis" {...axisProps} width={160} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="mentions" name="Prescriptions" radius={[0, 4, 4, 0]} barSize={16}>
              {top15.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'diagnosis', value: r.diagnosis, from: range.from, to: range.to })}
        title="Diagnoses"
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`top-diagnoses-${range.from}-to-${range.to}`}
        footnote="Compound entries like 'HTN, DM' are counted once per condition."
      />

      <ReportPrintLayout
        title="Top Diagnoses"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[{ title: 'Diagnoses', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default TopDiagnosesReport;
